import { IErrorPayload, useUrlQueryParams } from '@color/lib';
import { useCallback } from 'react';

import { VariantConfirmationState, VariantType } from '../types';

// Internal page numbers are zero-based to match what `@mui/x-data-grid` expects. URLs are 1-based
// (`?page=1` is the first page) to match user expectations and the convention used across the web.
export const DEFAULT_PAGE_NUMBER = 0;
export const DEFAULT_ROWS_PER_PAGE = 25;
export const ROWS_PER_PAGE_OPTIONS = [25, 50, 100] as const;

export const ALL_STATES_FILTER_VALUE = 'all' as const;
export type StateFilterOption = VariantConfirmationState | typeof ALL_STATES_FILTER_VALUE;

export const ALL_VARIANT_TYPES_FILTER_VALUE = 'all' as const;
export type VariantTypeFilterOption = VariantType | typeof ALL_VARIANT_TYPES_FILTER_VALUE;

const VARIANT_TYPE_FILTER_OPTIONS = new Set<VariantTypeFilterOption>([
  ...Object.values(VariantType),
  ALL_VARIANT_TYPES_FILTER_VALUE,
]);

function pageNumberToUrl(zeroBasedPage: number): string {
  return String(zeroBasedPage + 1);
}

const DEFAULT_PARAMS = {
  state: VariantConfirmationState.NEW,
  kitBarcode: '',
  variantType: ALL_VARIANT_TYPES_FILTER_VALUE,
  page: pageNumberToUrl(DEFAULT_PAGE_NUMBER),
  pageSize: String(DEFAULT_ROWS_PER_PAGE),
} as const;

export type QueryParams = {
  [K in keyof typeof DEFAULT_PARAMS]: (typeof DEFAULT_PARAMS)[K] extends string
    ? string
    : (typeof DEFAULT_PARAMS)[K];
};

// Drops fields whose value equals the corresponding default so the URL only reflects
// non-default state. `useUrlQueryParams` filters out `undefined` values before serialising.
export function normalizeQueryParams(params: QueryParams): Partial<QueryParams> {
  return Object.fromEntries(
    Object.entries(params).map(([k, v]) => [
      k,
      v === DEFAULT_PARAMS[k as keyof QueryParams] ? undefined : v,
    ])
  ) as Partial<QueryParams>;
}

export function parsePageNumberFromUrl(value: string): number {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 1 ? parsed - 1 : DEFAULT_PAGE_NUMBER;
}

export function parseRowsPerPageFromUrl(value: string): number {
  const parsed = Number.parseInt(value, 10);
  return (ROWS_PER_PAGE_OPTIONS as readonly number[]).includes(parsed)
    ? parsed
    : DEFAULT_ROWS_PER_PAGE;
}

export function parseStateFromUrl(value: string): StateFilterOption {
  if (value === ALL_STATES_FILTER_VALUE) return ALL_STATES_FILTER_VALUE;
  if (Object.values(VariantConfirmationState).includes(value as VariantConfirmationState)) {
    return value as VariantConfirmationState;
  }
  return VariantConfirmationState.NEW;
}

export function parseVariantTypeFromUrl(value: string): VariantTypeFilterOption {
  if (VARIANT_TYPE_FILTER_OPTIONS.has(value as VariantTypeFilterOption)) {
    return value as VariantTypeFilterOption;
  }
  return ALL_VARIANT_TYPES_FILTER_VALUE;
}

interface UseListViewUrlStateInput {
  // Invoked whenever any setter writes to the URL. The caller typically uses this to reset
  // selection — when filters or page change, the previously selected rows are no longer relevant.
  onChange?: () => void;
}

export interface ListViewUrlState {
  stateFilter: StateFilterOption;
  kitBarcodeFilter: string;
  variantTypeFilter: VariantTypeFilterOption;
  pageNumber: number;
  rowsPerPage: number;
  updateFilters: (updates: Partial<QueryParams>) => void;
  setPageNumber: (next: number) => void;
  setRowsPerPage: (next: number) => void;
}

export function useListViewUrlState(input: UseListViewUrlStateInput = {}): ListViewUrlState {
  const { onChange } = input;
  const { queryParams, setQueryParams } =
    useUrlQueryParams<keyof typeof DEFAULT_PARAMS>(DEFAULT_PARAMS);

  const stateFilter = parseStateFromUrl(queryParams.state);
  const kitBarcodeFilter = queryParams.kitBarcode ?? '';
  const variantTypeFilter = parseVariantTypeFromUrl(queryParams.variantType);
  const pageNumber = parsePageNumberFromUrl(queryParams.page);
  const rowsPerPage = parseRowsPerPageFromUrl(queryParams.pageSize);

  const updateFilters = useCallback(
    (updates: Partial<QueryParams>) => {
      setQueryParams(
        normalizeQueryParams({
          ...queryParams,
          ...updates,
          page: pageNumberToUrl(DEFAULT_PAGE_NUMBER),
        })
      );
      onChange?.();
    },
    [queryParams, setQueryParams, onChange]
  );

  const setPageNumber = useCallback(
    (next: number) => {
      setQueryParams(normalizeQueryParams({ ...queryParams, page: pageNumberToUrl(next) }));
      onChange?.();
    },
    [queryParams, setQueryParams, onChange]
  );

  const setRowsPerPage = useCallback(
    (next: number) => {
      setQueryParams(
        normalizeQueryParams({
          ...queryParams,
          page: pageNumberToUrl(DEFAULT_PAGE_NUMBER),
          pageSize: String(next),
        })
      );
      onChange?.();
    },
    [queryParams, setQueryParams, onChange]
  );

  return {
    stateFilter,
    kitBarcodeFilter,
    variantTypeFilter,
    pageNumber,
    rowsPerPage,
    updateFilters,
    setPageNumber,
    setRowsPerPage,
  };
}

interface PaginationSafetyHandlersInput {
  pageNumber: number;
  rowsPerPage: number;
  setPageNumber: (next: number) => void;
}

interface PaginationSafetyHandlers {
  // Loose structural type so the factory stays decoupled from the concrete query result shape.
  onSuccess: (data: { count: number }) => void;
  onError: (error: IErrorPayload) => void;
}

/**
 * Builds react-query callbacks that snap `pageNumber` back into the valid range whenever the
 * server reports it isn't. Two cases:
 *
 *  - **Out of range after a successful fetch.** Happens when a shared link points past the last
 *    page, items were deleted out-of-band, or the user grew `rowsPerPage` until the page count
 *    shrank. `onSuccess` only fires when react-query resolves with fresh data — never for the
 *    `keepPreviousData` placeholder — so we don't need to guard against stale counts.
 *  - **404 from DRF's PageNumberPagination.** Out-of-range requests return 404 rather than an
 *    empty page; recovery is to jump back to page 0.
 *
 * Exposed as plain callbacks instead of `useEffect`s so the URL update flows directly from the
 * server event rather than from a render-driven sync — no effect ping-pong, no race against
 * `keepPreviousData`, and the snap only fires once per relevant resolution.
 */
export function createPaginationSafetyHandlers(
  input: PaginationSafetyHandlersInput
): PaginationSafetyHandlers {
  const { pageNumber, rowsPerPage, setPageNumber } = input;
  return {
    onSuccess: (data) => {
      if (data.count === 0) {
        if (pageNumber !== DEFAULT_PAGE_NUMBER) setPageNumber(DEFAULT_PAGE_NUMBER);
        return;
      }
      const lastPage = Math.max(0, Math.ceil(data.count / rowsPerPage) - 1);
      if (pageNumber > lastPage) setPageNumber(lastPage);
    },
    onError: (error) => {
      if (error.status === 404 && pageNumber !== DEFAULT_PAGE_NUMBER) {
        setPageNumber(DEFAULT_PAGE_NUMBER);
      }
    },
  };
}
